import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

const StoryCard = ({ story, type = 'insight', storyType, isPublic = false, authUser }) => {
  const [isArticleExpanded, setIsArticleExpanded] = useState(false);
  const navigate = useNavigate();

  const effectiveStoryType = storyType || story.story_type || type;
  const displayType = story.displayType || type || effectiveStoryType;

  const toggleArticleExpanded = () => {
    setIsArticleExpanded(!isArticleExpanded);
  };

  const handleStoryClick = (e) => {
    // Don't navigate if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    
    // Navigate to story page if article exists
    if (story.article && story.article.id) {
      navigate(`/story/${story.article.id}`);
    }
  };
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeIcon = () => {
    switch(displayType) {
      case 'local': return '📍';
      case 'national': return '🌐';
      case 'week': return '🌎';
      case 'day': return '📅';
      default: return '🚀';
    }
  };

  const getTypeLabel = () => {
    const queryLabel = story.query_text || story.article?.query_text;
    if (queryLabel && queryLabel.trim()) return queryLabel.trim();
    switch(displayType) {
      case 'local': return 'Local Story';
      case 'national': return 'National Story';
      case 'week': return 'Weekly Story';
      case 'day': return "Today's News";
      default: return 'Strategic Insight';
    }
  };

  return (
    <article 
      className="story-card" 
      onClick={handleStoryClick}
      style={{ cursor: story.article && story.article.id ? 'pointer' : 'default' }}
    >
      <div className="story-card-header">
        <div className="d-flex align-items-center gap-2">
          <div className="story-type-badge">
            <span className="story-type-icon">{getTypeIcon()}</span>
            <span className="story-type-label">{getTypeLabel()}</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {story.article && story.article.date && (
            <time className="story-date" dateTime={story.article.date}>
              {formatDate(story.article.date)}
            </time>
          )}
          {story.article && story.article.id && (
            <FavoriteButton 
              articleId={story.article.id}
              storyType={effectiveStoryType}
              authUser={authUser}
            />
          )}
        </div>
      </div>

      {(story.article && (story.article.image || story.article?.query_photo_url || story.query_photo_url)) && (
        <div className="story-image-container">
          <img 
            src={story.article.image || story.article?.query_photo_url || story.query_photo_url}
            alt={story.article?.title || story.ai_takeaway_title || 'Industry Update'}
            className="story-image"
          />
        </div>
      )}

      <div className="story-content">
        <h2 className="story-title">
          {
            story.article?.title || story.ai_takeaway_title || 'Industry Update'
          } {story.article && story.article.id && (
            <i className="fas fa-external-link-alt story-link-icon"></i>
          )}
        </h2>

        {story.ai_takeaway_text && (
          <div className="story-takeaway">
            <h3 className="takeaway-label">Key Takeaway</h3>
            <p className="takeaway-text">{story.ai_takeaway_text}</p>
          </div>
        )}

        {story.ai_summary && (
          <div className="story-summary">
            <h3 className="summary-label">Executive Summary</h3>
            <p className="summary-text">{story.ai_summary}</p>
          </div>
        )}

        {story.ai_key_points && (
          <div className="story-key-points">
            <h3 className="key-points-label">Key Points</h3>
            <p className="key-points-text">{story.ai_key_points}</p>
          </div>
        )}

        {story.ai_excerpt && (
          <div className="story-excerpt">
            <h3 className="excerpt-label">Excerpt</h3>
            <blockquote className="excerpt-text">
              "{story.ai_excerpt}"
            </blockquote>
          </div>
        )}

        {/* Source Article Section */}
        {story.article && (
          <div className="source-article-section">
            <h3 className="source-label">Source Article</h3>
            <div className="source-details">
              <h4 className="source-title">
                {isPublic && story.article.url ? (
                  <a 
                    href={story.article.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="source-link"
                  >
                    {
                    story.article?.title || story.ai_takeaway_title || 'Industry Update'
                    } {story.article && story.article.id && (
                      <i className="fas fa-external-link-alt story-link-icon"></i>
                    )}
                  </a>
                ) : (
                  story.article.title
                )}
              </h4>
              
              <div className="source-meta">
                {story.article.author && (
                  <div className="source-author">
                    <i className="fas fa-user"></i>
                    <span>By {story.article.author}</span>
                  </div>
                )}
                
                {story.article.source && (
                  <div className="source-publication">
                    <i className="fas fa-newspaper"></i>
                    <span>{story.article.source}</span>
                  </div>
                )}
                
                {story.article.date && (
                  <div className="source-date">
                    <i className="fas fa-calendar"></i>
                    <span>{formatDate(story.article.date)}</span>
                  </div>
                )}
              </div>

              {/* Show/Hide Full Article Text and Read at Source for Public Users */}
              {isPublic && story.article && story.article.full_text && (
                <div className="article-content">
                  <div className="article-actions-row">
                    <button 
                      className="expand-toggle"
                      onClick={toggleArticleExpanded}
                      aria-expanded={isArticleExpanded}
                    >
                      <span>{isArticleExpanded ? 'Hide' : 'Show'} Full Article</span>
                      <i className={`fas fa-chevron-${isArticleExpanded ? 'up' : 'down'}`}></i>
                    </button>
                    
                    {story.article.url && (
                      <a 
                        href={story.article.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="read-source-btn"
                      >
                        Read at Source
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    )}
                  </div>
                  
                  {isArticleExpanded && (
                    <div className="article-full-text">
                      <div className="article-text-content">
                        {story.article.full_text}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fallback: Show Read at Source if no full text available */}
              {isPublic && story.article.url && !story.article.full_text && (
                <div className="article-actions">
                  <a 
                    href={story.article.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="read-source-btn"
                  >
                    Read at Source
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default StoryCard;